/**
 * Core types and enums for the Page-based navigation system.
 * This system allows games to define their pages and navigation structure
 * in a unified way that works across web and mobile platforms.
 */

import { IconName } from '../NavBar/IconTypes';

/**
 * Enumeration of common page identifiers shared across games.
 * These represent reusable page components that multiple games can use.
 */
export enum CommonPageId {
  Help = "help",
  Archive = "archive", 
  More = "more",
  DailyStreak = "daily-streak", // Modal page for daily streak celebrations
  Terms = "terms", // Terms of Service and Privacy Policy
  Login = "login", // Login modal for magic link authentication
  AuthError = "auth-error", // Authentication error page for invalid/expired magic links
  MagicLinkCallback = "magic-link-callback", // Full-page callback handler for magic link verification
  CloseTab = "close-tab", // Page shown when original tab can't auto-close after magic link verification
  NotificationPermission = "notification-permission", // Modal page for requesting notification permissions
}

/**
 * Simple navigation item for page configurations.
 * This is different from NavBarItem to keep page system decoupled.
 */
export interface PageNavItem {
  label: string;
  icon: IconName | string; // Icon name/identifier - can be enum or string for flexibility
  order: number; // Display order in navigation
}

/**
 * Base props that all page components should accept.
 * Provides consistent interface for page functionality.
 * Game settings and player data should be accessed via Redux store.
 */
export interface BasePageProps {
  /** Callback to close/dismiss the page */
  onRequestClose?: () => void;
  /** Page identifier */
  pageId?: string;
}

/**
 * Configuration for a page in the page system.
 * Defines rendering behavior and navigation presence.
 */
export interface PageConfig {
  /** Unique identifier for the page */
  id: string | CommonPageId;
  /** If present, render the page as a modal overlay */
  modalPageConfig?: ModalPageConfig;
  /** Navigation item configuration, null if not shown in nav bar */
  navItem: PageNavItem | null;
}

export interface ModalPageConfig {
  /** Human readable title of page. */
  humanReadableTitle?: string;
  /** Image at the top of the modal. */
  headerImage?: string;
}
